import { useCallback, useEffect } from 'react'
import { ralphApi } from '../api'
import { useAppStore } from '../stores/app-store'

export function GitHubPanel() {
  const {
    authenticated,
    deliveryMode,
    selectedBaseBranch,
    selectedRepoFullName,
    githubError,
    githubLoading,
    githubRepos,
    githubStatus,
    setDeliveryMode,
    setGitHubError,
    setGitHubLoading,
    setGitHubRepos,
    setGitHubStatus,
    setSelectedBaseBranch,
    setSelectedRepoFullName
  } = useAppStore()

  const refreshGitHub = useCallback(async () => {
    setGitHubLoading(true)
    setGitHubError('')

    try {
      const status = await ralphApi.getGitHubStatus()
      setGitHubStatus(status)

      if (status.reposSupported) {
        const repos = await ralphApi.listGitHubRepos()
        setGitHubRepos(repos)
      } else {
        setGitHubRepos([])
      }
    } catch (err) {
      setGitHubStatus(null)
      setGitHubRepos([])
      setGitHubError(err instanceof Error ? err.message : 'Failed to load GitHub status')
    } finally {
      setGitHubLoading(false)
    }
  }, [setGitHubError, setGitHubLoading, setGitHubRepos, setGitHubStatus])

  useEffect(() => {
    if (authenticated) {
      void refreshGitHub()
    }
  }, [authenticated, refreshGitHub])

  const selectedRepo = githubRepos.find((repo) => repo.fullName === selectedRepoFullName) ?? null

  return (
    <div className="card config-panel">
      <div className="card-header">
        <span className="card-title">GitHub</span>
        <button className="btn-small" onClick={() => void refreshGitHub()} disabled={githubLoading}>
          {githubLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="github-status-row">
        <span className={`status-pill ${githubStatus?.configured ? 'is-ok' : 'is-warn'}`}>
          {githubStatus?.configured ? githubStatus.authMode.replace('-', ' ') : 'not configured'}
        </span>
        {githubStatus?.installUrl && (
          <a
            className="btn-small github-link-btn"
            href={githubStatus.installUrl}
            target="_blank"
            rel="noreferrer"
          >
            Connect repos
          </a>
        )}
      </div>

      {(githubStatus?.message || githubError) && (
        <div className="github-message">{githubError || githubStatus?.message}</div>
      )}

      <div className="config-grid">
        <div className="config-item full-width">
          <label>Connected Repo</label>
          <select
            className="config-input"
            value={selectedRepoFullName}
            onChange={(e) => {
              const repo = githubRepos.find((item) => item.fullName === e.target.value)
              setSelectedRepoFullName(e.target.value)
              if (repo) {
                setSelectedBaseBranch(repo.defaultBranch)
              }
            }}
            disabled={!githubRepos.length}
          >
            {!githubRepos.length && <option value="">No repos available</option>}
            {githubRepos.map((repo) => (
              <option key={repo.id} value={repo.fullName}>
                {repo.fullName}
              </option>
            ))}
          </select>
        </div>

        <div className="config-item">
          <label>Base Branch</label>
          <input
            type="text"
            className="config-input"
            value={selectedBaseBranch}
            onChange={(e) => setSelectedBaseBranch(e.target.value)}
            placeholder={selectedRepo?.defaultBranch || 'main'}
            disabled={!selectedRepo}
          />
        </div>

        <div className="config-item">
          <label>Delivery</label>
          <select
            className="config-input"
            value={deliveryMode}
            onChange={(e) => setDeliveryMode(e.target.value as 'download' | 'draft-pr')}
          >
            <option value="draft-pr">Draft PR</option>
            <option value="download">Download only</option>
          </select>
        </div>
      </div>
    </div>
  )
}
